import React from "react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  TableSortLabel,
  useTheme,
  IconButton,
} from "@mui/material";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { DateConverter } from "../../../../../utils/DateConverter";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import pencil_uderline from "../../../../../assets/pencil_uderline.png";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
interface DashboardUnpaidSamplesTableProps {
  unpaid_samples: any[];
  maxHeight: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
  onRowsPerPageChange: (limit: number) => void;
  onActionClick?: (id: string) => void;
}

const DashboardUnpaidSamplesTable: React.FC<
  DashboardUnpaidSamplesTableProps
> = (props: DashboardUnpaidSamplesTableProps) => {
  const handleChangePage = (event: unknown, newPage: number) => {
    props.onPageChange(newPage);
  };
  const { userStore } = useStores();
  const theme = useTheme();
  const handleRowsChange = (value: number) => {
    props.onPageChange(1);
    props.onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (props.sortBy === property) {
      return props.sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const handleDownload = () => {
    const headers = [
      "Order ID",
      "Order Label",
      "Customer",
      "Sales Admin",
      "Archived",
      "Archived By",
      "Archived Date",
      "Purchase Rate (₹)",
      "Order Value (₹)",
      "Order Created Date",
      "Project Created Date",
    ];

    const rows = props.unpaid_samples?.map((row) => [
      row.order_id,
      row.order_label || "",
      row.customer || "",
      row.sales_poc || "",
      row.archived === 1 ? "Yes" : "No",
      row.archived_by || "",
      row.archived_date ? formatDate(row.archived_date) : "",
      row.purcahse_rate || "",
      row.order_value || "",
      row.order_created_date ? formatDate(row.order_created_date) : "",
      row.project_created_date ? formatDate(row.project_created_date) : "",
    ]);

    const escapeCsv = (value: any) => {
      const cell = String(value ?? "").replace(/"/g, '""');
      return `"${cell}"`;
    };

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\n");

    const now = formatDate(new Date());
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Unpaid_Samples_${now}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ marginTop: 4 }}>
      <Box
        sx={{
          backgroundColor: theme.palette.customColors.white,
          padding: 4,
          borderRadius: 2,
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <CustomTypography variant="heading4">Unpaid Samples</CustomTypography>
          <Box display="flex" justifyContent="flex-end">
            <button
              onClick={handleDownload}
              style={{
                padding: "10px 16px",
                backgroundColor: theme.palette.customColors.purple,
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme.palette.customColors.purple;
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme.palette.customColors.purple;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 6px rgba(0, 0, 0, 0.1)";
              }}
            >
              <FontAwesomeIcon icon={faDownload} fontSize="0.875rem" />
              Download Data
            </button>
          </Box>
        </Box>

        <TableContainer sx={{ maxHeight: props.maxHeight }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                    position: "sticky",
                    left: 0,
                    zIndex: 15,
                    backgroundColor: theme.palette.customColors.white,
                  }}
                >
                  <TableSortLabel
                    active={props.sortBy === "o.id"}
                    direction={props.sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      props.onSortChange(
                        "o.id",
                        props.sortBy === "o.id" &&
                          props.sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon("o.id")}</span>}
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    ORDER ID
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  minWidth="200px"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                    maxWidth: 400,
                  }}
                >
                  ORDER LABEL
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  CUSTOMER
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  SALES ADMIN
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  ARCHIVED
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  ARCHIVED BY
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  ARCHIVED DATE
                </AppTableHeaderCell>
                {userStore.hasPermission(EmployeeRoles.CHANGE_COST_PRICE) && (
                  <AppTableHeaderCell
                    sx={{
                      color: theme.palette.customColors.tableGrayHeadingColor,
                      fontWeight: "bold",
                    }}
                  >
                    PURCHASE RATE (₹)
                  </AppTableHeaderCell>
                )}
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  ORDER VALUE (₹)
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  LAST SHIPMENT ID
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  LAST SHIPMENT DATE
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  <TableSortLabel
                    active={props.sortBy === "o.created_date"}
                    direction={props.sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      props.onSortChange(
                        "o.created_date",
                        props.sortBy === "o.created_date" &&
                          props.sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("o.created_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    ORDER CREATED DATE
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                  }}
                >
                  <TableSortLabel
                    active={props.sortBy === "p.created_date"}
                    direction={props.sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      props.onSortChange(
                        "p.created_date",
                        props.sortBy === "p.created_date" &&
                          props.sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("p.created_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    PROJECT CREATED DATE
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    fontWeight: "bold",
                    position: "sticky",
                    right: 0,
                    zIndex: 17,
                    backgroundColor: theme.palette.customColors.white,
                  }}
                >
                  RETURN PROCESS
                </AppTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.unpaid_samples.map((row) => (
                <TableRow key={row.id}>
                  <AppTableRowCell
                    sx={{
                      cursor: "pointer",
                      fontWeight: "bold",
                      position: "sticky",
                      left: 0,
                      zIndex: 10,
                      backgroundColor: theme.palette.customColors.white,
                    }}
                  >
                    {row.order_id}
                  </AppTableRowCell>
                  <AppTableRowCell
                    minWidth="200px"
                    sx={{
                      fontSize: "0.8rem",
                      maxWidth: 300,
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {row.order_label}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {row.customer}
                  </AppTableRowCell>

                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {row.sales_poc}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {row.archived === 1 ? "Yes" : "No"}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {row.archived_by || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {formatDate(row.archived_date)}
                  </AppTableRowCell>
                  {userStore.hasPermission(EmployeeRoles.CHANGE_COST_PRICE) && (
                    <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                      {row.purcahse_rate}
                    </AppTableRowCell>
                  )}
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {row.order_value}
                  </AppTableRowCell>

                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {row.last_shipment_id || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {formatDate(row.last_shipment_date)}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {DateConverter(row.order_created_date)}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {row.project_created_date
                      ? DateConverter(row.project_created_date)
                      : "-"}
                  </AppTableRowCell>
                  <AppTableRowCell
                    sx={{
                      fontSize: "0.8rem",
                      position: "sticky",
                      right: 0,
                      zIndex: 10,
                      backgroundColor: theme.palette.customColors.white,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => props.onActionClick?.(row.order_id!)}
                    >
                      <img src={pencil_uderline} alt="pencil_uderline" />
                    </IconButton>
                  </AppTableRowCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <AppTablePagenation
          fontSize="0.8rem"
          pageNo={props.page_no || 1}
          limit={props.limit || 0}
          totalCount={props.total_count || 0}
          onPageChange={handleChangePage}
          handleRowsChange={handleRowsChange}
        />
      </Box>
    </Box>
  );
};

export default DashboardUnpaidSamplesTable;
